import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { MessageCircle } from "lucide-react";
import { useLang } from "@/App";
import { Logo } from "@/components/Navbar";
import ParallaxLayer from "@/components/ParallaxLayer";

const CARD_GLOWS = {
    neon: "hover:border-[#d7ff35]/60 hover:shadow-[0_0_34px_rgba(215,255,53,0.14)]",
    lucy: "hover:border-[#ff4bd8]/60 hover:shadow-[0_0_34px_rgba(255,75,216,0.14)]",
    david: "hover:border-[#fcee0a]/60 hover:shadow-[0_0_34px_rgba(252,238,10,0.14)]",
    rebecca: "hover:border-[#49f5e8]/60 hover:shadow-[0_0_34px_rgba(73,245,232,0.14)]",
    maine: "hover:border-[#d7ff35]/60 hover:shadow-[0_0_34px_rgba(215,255,53,0.14)]",
    kiwi: "hover:border-[#8b5cf6]/60 hover:shadow-[0_0_34px_rgba(139,92,246,0.14)]",
    glitch: "hover:border-[#fcee0a]/70 hover:shadow-[0_0_34px_rgba(252,238,10,0.18)]",
};

export default function CharactersPage() {
    const { t, persona, setPersona } = useLang();
    const navigate = useNavigate();

    const talk = (id) => {
        setPersona(id);
        navigate("/chat");
    };

    return (
        <div className="relative pt-16 min-h-screen overflow-hidden" data-testid="characters-page">
            <div className="kana kana-watermark absolute right-2 top-24 text-[7rem] lg:text-[10rem] leading-none pointer-events-none" aria-hidden="true">
                キャラ
            </div>
            <ParallaxLayer
                speed={-70}
                testid="characters-parallax-1"
                className="hidden lg:block absolute right-[-60px] top-[52%] w-[320px] h-[480px] char-bleed bg-cover bg-center opacity-[0.1] pointer-events-none"
                style={{ backgroundImage: "url(https://images.unsplash.com/photo-1581578701651-1f7cff5960aa?q=80&w=700&auto=format&fit=crop)" }}
            />
            <ParallaxLayer
                speed={80}
                testid="characters-parallax-2"
                className="hidden lg:block absolute left-[-70px] bottom-[4%] w-[280px] h-[420px] char-bleed-r bg-cover bg-center opacity-[0.09] pointer-events-none"
                style={{ backgroundImage: "url(https://images.unsplash.com/photo-1620215175664-cb9a6f5b6103?q=80&w=700&auto=format&fit=crop)" }}
            />
            <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 lg:py-24">
                <motion.div
                    initial={{ opacity: 0, y: 30 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
                    className="mb-12 max-w-2xl"
                >
                    <div className="font-mono2 text-[11px] tracking-[0.3em] text-[#fcee0a] edg-text flex items-center gap-3">
                        {t.characters.kicker}
                        <span className="warning-stripes w-12 h-1.5 opacity-70" />
                    </div>
                    <h1 className="font-display font-bold uppercase tracking-wide text-2xl sm:text-3xl lg:text-4xl mt-3">
                        {t.characters.title}
                    </h1>
                    <p className="text-sm sm:text-base text-slate-400 mt-4 leading-relaxed">{t.characters.sub}</p>
                </motion.div>

                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="characters-grid">
                    {t.chat.personas.map((p, i) => (
                        <motion.div
                            key={p.id}
                            data-testid={`character-card-${p.id}`}
                            initial={{ opacity: 0, y: 30 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.6, delay: i * 0.08, ease: [0.16, 1, 0.3, 1] }}
                            className={`glow-card rounded-2xl p-6 transition-all duration-300 ${CARD_GLOWS[p.id] || ""}`}
                        >
                            <div className="flex items-center gap-4">
                                <span
                                    className={`w-20 h-20 rounded-full overflow-hidden border-2 shrink-0 avatar-breathe ${
                                        persona === p.id ? "border-[#fcee0a] shadow-[0_0_20px_rgba(252,238,10,0.4)]" : "border-[#2c3a5c]"
                                    }`}
                                >
                                    {p.avatar ? (
                                        <img src={p.avatar} alt={p.name} className="w-full h-full object-cover" />
                                    ) : (
                                        <span className="w-full h-full grid place-items-center bg-[#0e1322]">
                                            <Logo size={40} />
                                        </span>
                                    )}
                                </span>
                                <div>
                                    <div className="font-display font-bold tracking-widest text-base">{p.name}</div>
                                    <div className="font-mono2 text-[9px] tracking-[0.25em] text-slate-500 mt-1">{p.tag}</div>
                                </div>
                            </div>
                            <p className="text-xs text-slate-400 leading-relaxed mt-4 min-h-[54px] line-clamp-3 italic">
                                “{t.chat.greetings[p.id]}”
                            </p>
                            <button
                                data-testid={`character-talk-${p.id}`}
                                onClick={() => talk(p.id)}
                                className="mt-4 w-full flex items-center justify-center gap-2 border border-[#fcee0a]/60 text-[#fcee0a] font-mono2 text-[10px] tracking-[0.2em] uppercase py-2.5 rounded-xl hover:bg-[#fcee0a]/10 transition-colors"
                            >
                                <MessageCircle size={13} />
                                {t.characters.talk}
                            </button>
                        </motion.div>
                    ))}
                </div>
            </div>
        </div>
    );
}
